import LatLonEllipsoidal, { Dms } from 'geodesy/latlon-ellipsoidal-vincenty';
import VincentyDirect from 'geodesy/latlon-ellipsoidal-vincenty';
import { PixoSpatialCoord, PixoSpatial } from './Pixospatial';
import * as turf from '@turf/turf';
import * as math from 'mathjs';
import { GetNearestPointOnInfiniteLine, GetNearestPointOnSegment, Vector } from './drawing/vector-utils';

export class PixoSpatialVector {
  pixo: PixoSpatial;
  from: PixoSpatialCoord;
  to: PixoSpatialCoord;
  constructor(pixo: PixoSpatial, from: PixoSpatialCoord, to: PixoSpatialCoord) {
    this.from = from;
    this.to = to;
    this.pixo = pixo;
  }
  get distance(): number {
    const lat1 = this.from.spatial.lat;
    const lon1 = this.from.spatial.lng;
    const lat2 = this.to.spatial.lat;
    const lon2 = this.to.spatial.lng;
    const point1 = new LatLonEllipsoidal(lat1, lon1); // Initial point of the vector
    const point2 = new LatLonEllipsoidal(lat2, lon2); // Final point of the vector
    return point1.distanceTo(point2);
  };

  get bearingTo(): number {
    const lat1 = this.from.spatial.lat;
    const lon1 = this.from.spatial.lng;
    const lat2 = this.to.spatial.lat;
    const lon2 = this.to.spatial.lng;
    const point1 = new LatLonEllipsoidal(lat1, lon1); // Initial point of the vector
    const point2 = new LatLonEllipsoidal(lat2, lon2); // Final point of the vector
    return point1.initialBearingTo(point2);
  };
  get bearingFrom(): number {
    const lat1 = this.from.spatial.lat;
    const lon1 = this.from.spatial.lng;
    const lat2 = this.to.spatial.lat;
    const lon2 = this.to.spatial.lng;
    const point1 = new LatLonEllipsoidal(lat1, lon1); // Initial point of the vector
    const point2 = new LatLonEllipsoidal(lat2, lon2); // Final point of the vector
    return point1.finalBearingTo(point2);
  };

  RotateTo(angleDeg: number): PixoSpatialVector | undefined {
    const vector = this.ToVector();
    if(!vector) return undefined;
    vector.rotateTo(angleDeg);
    const newPt = vector.to;
    const coord = this.MeterCoordToCoord(newPt);
    this.to.UpdateSpatial(coord.spatial.lng, coord.spatial.lat);
    return this;
  }
  RotateFrom(angleDeg: number): PixoSpatialVector | undefined {
    const vector = this.ToVector();
    if(!vector) return undefined;
    vector.rotateTo(angleDeg);
    const newPt = vector.from;
    const coord = this.MeterCoordToCoord(newPt);
    this.from.UpdateSpatial(coord.spatial.lng, coord.spatial.lat);
    return this;
  }
  RotateToRelative(angleDeg: number, angleType: 'bearing' | 'math' = 'bearing'): PixoSpatialVector {
    const originalBearing = this.bearingTo;
    let adjustedAngleDeg = originalBearing + angleDeg;
    if (angleType === 'bearing') {
      this.RotateTo(90 - originalBearing - angleDeg);
    } else {
      this.RotateTo(angleDeg);
    }
    return this;
  }
  RotateFromRelative(angleDeg: number, angleType: 'bearing' | 'math' = 'bearing'): PixoSpatialVector {
    const originalBearing = this.bearingFrom;
    let adjustedAngleDeg = originalBearing + angleDeg;
    if (angleType === 'bearing') {
      this.RotateFrom(originalBearing + angleDeg);
    } else {
      this.RotateFrom((90 - originalBearing - angleDeg));
    }
    return this;
  }
  NearestPoint(coord: PixoSpatialCoord) {
    const fromToBearing = this.bearingTo;
    const fromPoint = this.CoordToLLE(this.from);
    const externalPoint = this.CoordToLLE(coord);
    const externalToFromBearing = externalPoint.initialBearingTo(fromPoint);
    const fromToExternalDistance = fromPoint.distanceTo(externalPoint);

    const dotProduct = fromToExternalDistance * Math.cos(Math.abs(externalToFromBearing - fromToBearing));
    const crossProduct = fromToExternalDistance * Math.sin(Math.abs(externalToFromBearing - fromToBearing));
    const perpendicularDistance = Math.abs(crossProduct);

    const projection = (dotProduct / this.distance) * Math.cos(fromToBearing);

    const intersection = new LatLonEllipsoidal(this.from.spatial.lat, this.from.spatial.lng).destinationPoint(projection, fromToBearing);

    return this.pixo.PixoSpatialCoord(this.pixo.SpatialCoord({ lng: intersection.lon, lat: intersection.lat }));
  }
  Intersect(vectorB: PixoSpatialVector): PixoSpatialCoord | undefined {
    const pts = turf.lineIntersect(this.ToTurfLine(), vectorB.ToTurfLine());
    if (!pts?.features?.length) return undefined;
    const pt = pts.features.shift();
    if(!pt) return undefined;
    const [lng, lat] = pt.geometry!.coordinates;
    return this.pixo.PixoSpatialCoord(this.pixo.SpatialCoord({ lng: lng, lat: lat }));
  }
  CoordToLLE(coord: PixoSpatialCoord): LatLonEllipsoidal {
    return new LatLonEllipsoidal(coord.spatial.lat, coord.spatial.lng);
  }
  ToTurfLine(vector?: PixoSpatialVector) {
    if (!vector) vector = this;
    const fromPt = this.CoordToTurf(vector.from);
    const toPt = this.CoordToTurf(vector.to);
    return turf.lineString([[vector.from.spatial.lng, vector.from.spatial.lat], [vector.to.spatial.lng, vector.to.spatial.lat]]);

  }
  CoordToTurf(coord: PixoSpatialCoord) {
    return turf.point([coord.spatial.lng, coord.spatial.lat]);
  }
  CoordToTurfMeters(coord: PixoSpatialCoord) {
    return turf.toMercator(turf.point([coord.spatial.lng, coord.spatial.lat]));
  }
  TurfMetersToCoord(turfMeters: any): PixoSpatialCoord {
    const [lng, lat] = turf.toWgs84(turfMeters).geometry.coordinates;
    return this.pixo.PixoSpatialCoord(this.pixo.SpatialCoord({ lng: lng, lat: lat }));
  }
  ToMeterMatrix(coord: PixoSpatialCoord): math.Matrix | undefined{
    const meters =  this.CoordToTurfMeters(coord)?.geometry?.coordinates;
    if(!meters) return undefined;
    const [x, y] = meters;
    return math.matrix([x, y]);
  }
  MeterMatrixToCoord(matrix: math.Matrix): PixoSpatialCoord {
    return this.pixo.PixoSpatialCoord(this.pixo.SpatialCoord(matrix.get([0]), matrix.get([1])));
  }
  MeterCoordToCoord(meterCoord: math.Matrix) {
    const coord = this.TurfMetersToCoord(turf.point([meterCoord.get([0]), meterCoord.get([1])]));
    return coord;
  }
  ToVector() {
    const fromMatrix = this.ToMeterMatrix(this.from);
    const toMatrix = this.ToMeterMatrix(this.to);
    if(!fromMatrix  || !toMatrix) return undefined;
    return new Vector(fromMatrix, toMatrix);
  }
  GetNearestPoint(externalPoint: PixoSpatialCoord): PixoSpatialCoord | undefined {
    // Convert external point to mathjs matrix
    const extMeters = this.CoordToTurfMeters(externalPoint)?.geometry?.coordinates;
    const fromMeters = this.CoordToTurfMeters(this.from)?.geometry?.coordinates;
    if(!extMeters  || !fromMeters) return undefined;
    const fromMatrix = math.matrix([fromMeters[0], fromMeters[1]]);
    const toMeters = this.CoordToTurfMeters(this.to)?.geometry?.coordinates;
    if(!fromMatrix  || !toMeters) return undefined;
    const toMatrix = math.matrix([toMeters[0], toMeters[1]]);
    const pt = GetNearestPointOnSegment(fromMatrix, toMatrix, { x: extMeters[0], y: extMeters[1] });
    const turfMeters = turf.point([pt.x, pt.y]);
    const coord = this.TurfMetersToCoord(turfMeters);
    return coord;
  }
  GetNearestInfinitePoint(externalPoint: PixoSpatialCoord) {
    const extMeters = this.CoordToTurfMeters(externalPoint)?.geometry?.coordinates;
    const fromMeters = this.CoordToTurfMeters(this.from)?.geometry?.coordinates;
    const toMeters = this.CoordToTurfMeters(this.to)?.geometry?.coordinates;
    if(!extMeters  || !fromMeters || !toMeters) return undefined;
    const simpleVector = new Vector(math.matrix([fromMeters[0], fromMeters[1]]), math.matrix([toMeters[0], toMeters[1]]));
    const pt = GetNearestPointOnInfiniteLine(simpleVector, { x: extMeters[0], y: extMeters[1] });
    const turfMeters = turf.point([pt.x, pt.y]);
    const coord = this.TurfMetersToCoord(turfMeters);
    return coord;

  }
}
